import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { SquareArrowOutUpRight } from 'lucide-react';
import { S3Client, PutObjectCommand } from '@aws-sdk/client-s3';
import { supabase, S3_CONFIG } from '../config/supabase';
import ImageLibraryModal from '../components/ImageLibraryModal';

export default function WhoIsThisForPage() {
  const navigate = useNavigate();
  const [dedication, setDedication] = useState(null);
  const [chainType, setChainType] = useState(null);
  const [names, setNames] = useState(['']);
  const [recipientImage, setRecipientImage] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [uploadedImagePath, setUploadedImagePath] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showLibraryModal, setShowLibraryModal] = useState(false);
  const [imageValidationError, setImageValidationError] = useState(false);
  const fileInputRef = useRef(null);
  const uploadLabelRef = useRef(null);

  // Device detection for app store redirect
  const handleAppStoreRedirect = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    let appStoreUrl;
    
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      appStoreUrl = 'https://apps.apple.com/us/app/yesod/id6748472269';
    } else if (/android/i.test(userAgent)) {
      appStoreUrl = 'https://play.google.com/store/apps/details?id=us.yesod.app';
    } else {
      appStoreUrl = 'https://apps.apple.com/us/app/yesod/id6748472269';
    }
    
    window.open(appStoreUrl, '_blank');
  };

  useEffect(() => {
    const savedDedication = sessionStorage.getItem('chainDedication');
    const savedChainType = sessionStorage.getItem('chainType');
    
    if (!savedDedication) {
      navigate('/create-chain');
      return;
    }
    
    if (!savedChainType) {
      navigate('/create-chain/type');
      return;
    }
    
    setDedication(savedDedication);
    setChainType(savedChainType);

    // Don't load existing data - always start fresh
    // User should enter new data each time they create a chain
  }, [navigate]);

  const handleNameChange = (index, value) => {
    const newNames = [...names];
    newNames[index] = value;
    setNames(newNames);
  };

  const handleAddName = () => {
    setNames([...names, '']);
  };

  const handleRemoveName = (index) => {
    if (names.length > 1) {
      const newNames = names.filter((_, i) => i !== index);
      setNames(newNames);
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    
    if (!file) return;
    
    // Validate file
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      e.target.value = '';
      return;
    }
    
    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      alert('Only PNG and JPG files are allowed');
      e.target.value = '';
      return;
    }
    
    // Upload to Supabase Storage
    await uploadImageToSupabase(file);
  };

  const uploadImageToSupabase = async (file) => {
    setIsUploading(true);
    
    try {
      // Generate unique filename with timestamp
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop() || 'jpg';
      const fileName = `${timestamp}.${fileExtension}`;
      
      // For anonymous users, use a temp folder
      // Once user authenticates, we can move the image or update the path
      const filePath = `temp/${fileName}`;
      
      console.log('Uploading image to Supabase Storage:', filePath);
      
      // Read file as ArrayBuffer
      const arrayBuffer = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
      });
      
      const buffer = new Uint8Array(arrayBuffer);
      
      // Create S3 client
      const s3Client = new S3Client(S3_CONFIG);
      
      // Upload using PutObjectCommand
      const uploadCommand = new PutObjectCommand({
        Bucket: 'prayer-images',
        Key: filePath,
        Body: buffer,
        ContentType: file.type,
      });
      
      await s3Client.send(uploadCommand);
      console.log('✅ Upload successful!');
      
      // Get public URL
      const { data: urlData } = supabase.storage
        .from('prayer-images')
        .getPublicUrl(filePath);
      
      console.log('Public URL:', urlData.publicUrl);
      
      // Update state
      setImagePreviewUrl(urlData.publicUrl);
      setUploadedImagePath(filePath);
      setRecipientImage(file);
      setImageValidationError(false); // Clear validation error
      
    } catch (error) {
      console.error('❌ Upload error:', error);
      alert('Failed to upload image. Please try again.');
      
      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handlePickFromLibrary = () => {
    setShowLibraryModal(true);
  };

  const handleLibraryImageSelect = (imageUrl) => {
    // When user selects from library, just set the URL
    // No need to upload since it's already in Supabase Storage
    setImagePreviewUrl(imageUrl);
    setUploadedImagePath(null); // Library images don't need cleanup
    setRecipientImage(null);
    setShowLibraryModal(false);
    setImageValidationError(false); // Clear validation error
  };

  const handleRemoveImage = async () => {
    // Note: We don't delete temp images from storage here because:
    // 1. Anonymous users may not have delete permissions
    // 2. Temp images will be cleaned up by a backend job
    // 3. Just clear the UI state
    
    console.log('Removing image from UI (storage cleanup handled by backend):', uploadedImagePath);
    
    // Clear UI state
    setImagePreviewUrl(null);
    setRecipientImage(null);
    setUploadedImagePath(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (uploadLabelRef.current) {
      uploadLabelRef.current.classList.add('drag-over');
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    if (uploadLabelRef.current) {
      uploadLabelRef.current.classList.remove('drag-over');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (uploadLabelRef.current) {
      uploadLabelRef.current.classList.remove('drag-over');
    }
    
    const file = e.dataTransfer.files[0];
    if (file && fileInputRef.current) {
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      fileInputRef.current.files = dataTransfer.files;
      handleImageChange({ target: fileInputRef.current });
    }
  };

  const handleBack = () => {
    navigate('/create-chain/type');
  };

  const handleClose = () => {
    sessionStorage.removeItem('chainDedication');
    sessionStorage.removeItem('chainType');
    sessionStorage.removeItem('whoIsThisForData');
    navigate('/');
  };

  const handleNext = () => {
    const filledNames = names.filter(n => n.trim());
    
    if (filledNames.length === 0) {
      alert('Please enter at least one name');
      return;
    }

    // Validate image is uploaded/selected
    if (!imagePreviewUrl) {
      setImageValidationError(true);
      return;
    }

    // Clear error if validation passes
    setImageValidationError(false);

    // Save data to sessionStorage including the Supabase URL
    sessionStorage.setItem('whoIsThisForData', JSON.stringify({
      names: filledNames,
      imagePreviewUrl, // This is now the Supabase Storage URL
      uploadedImagePath // Store path for potential cleanup
    }));

    // Navigate to Step 4
    navigate('/create-chain/story');
  };

  if (!dedication || !chainType) {
    return null;
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <div className="sticky top-0 z-50 flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-white gap-4">
        <div className="flex items-center gap-3 flex-shrink-0">
          <img 
            src="https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/Yesod%20Logo%204K%20(1).png" 
            alt="Yesod" 
            className="h-10 w-auto object-contain flex-shrink-0"
          />
          <h1 className="text-xl font-bold text-black m-0 flex-shrink-0">Yesod</h1>
        </div>
        <button 
          onClick={handleAppStoreRedirect}
          className="bg-[#667eea] text-white px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap flex-shrink-0 ml-auto flex items-center gap-1.5 hover:bg-[#5568d3] transition-all cursor-pointer"
        >
          Continue in App
          <SquareArrowOutUpRight size={16} />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 px-5 py-8 pb-32">
        <h2 className="text-3xl font-bold text-[#667eea] mb-3 leading-tight">Who is this for?</h2>
        <p className="text-base text-gray-600 mb-8 leading-relaxed">
          {chainType === 'tehillim' 
            ? 'Add the name of the person you want people to say Tehillim for'
            : 'Add the name of the person you want people to dedicate their Hafrashat Challah to'
          }
        </p>

        {/* Name Inputs */}
        <div className="mb-8">
          {names.map((name, index) => (
            <div key={index} className="relative mb-3">
              <input
                type="text"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl text-base transition-all focus:border-[#667eea] focus:outline-none focus:shadow-[0_0_0_3px_rgba(102,126,234,0.1)]"
                placeholder="Enter name (e.g., Moshe ben Sarah)"
                value={name}
                onChange={(e) => handleNameChange(index, e.target.value)}
                dir="auto"
              />
              {names.length > 1 && (
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-red-500 text-2xl transition-colors"
                  onClick={() => handleRemoveName(index)}
                >
                  ×
                </button>
              )}
            </div>
          ))}
          
          <button type="button" className="text-[#667eea] font-semibold text-sm hover:underline transition-all" onClick={handleAddName}>
            + Add another name
          </button>
        </div>

        {/* Image Upload */}
        <div className="mb-8">
          <h3 className="text-base font-semibold text-gray-900 mb-3">Add a cover image</h3>
          
          <input
            type="file"
            ref={fileInputRef}
            accept="image/png,image/jpeg,image/jpg"
            className="hidden"
            onChange={handleImageChange}
            disabled={isUploading}
          />

          {isUploading ? (
            <div className="border-2 border-dashed border-gray-300 rounded-2xl p-10 text-center bg-gray-50 transition-all">
              <div className="text-gray-400 mb-4 flex justify-center">
                <div className="w-12 h-12 border-4 border-gray-200 border-t-[#667eea] rounded-full animate-spin"></div>
              </div>
              <p className="text-base font-semibold text-[#667eea] mb-1">Uploading image...</p>
              <p className="text-sm text-gray-600">Please wait</p>
            </div>
          ) : !imagePreviewUrl ? (
            <div 
              ref={uploadLabelRef}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-gray-300 rounded-2xl p-10 text-center bg-gray-50 transition-all hover:border-[#667eea] hover:bg-blue-50 cursor-pointer"
            >
              <div className="text-gray-400 mb-4 flex justify-center">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
              </div>
              <p className="text-base font-semibold text-[#667eea] mb-1">Tap to upload photo</p>
              <p className="text-sm text-gray-600 mb-6">PNG, JPG or PDF (max. 800x400px)</p>
              <div className="relative my-6 text-center">
                <div className="absolute left-0 right-0 top-1/2 h-px bg-gray-200"></div>
                <span className="relative bg-gray-50 px-3 text-sm text-gray-400 font-semibold">OR</span>
              </div>
              <button type="button" className="bg-gradient-to-br from-[#667eea] to-[#764ba2] text-white px-8 py-3.5 rounded-xl text-base font-semibold hover:translate-y-[-2px] hover:shadow-lg transition-all" onClick={(e) => { e.stopPropagation(); handlePickFromLibrary(); }}>
                Pick from Library
              </button>
            </div>
          ) : (
            <div className="relative rounded-2xl overflow-hidden group bg-gray-100 flex items-center justify-center" style={{ minHeight: '300px' }}>
              <img src={imagePreviewUrl} alt="Preview" className="w-full max-h-[300px] object-contain" />
              <button type="button" className="absolute top-3 right-3 w-10 h-10 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow-lg" onClick={handleRemoveImage}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
          )}
          
          {/* Validation Error Message */}
          {imageValidationError && (
            <div className="mt-4 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <span className="font-medium">You must add an image to continue</span>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="sticky bottom-0 left-0 right-0 px-5 py-5 border-t border-gray-200 bg-white flex flex-col gap-4 z-40 mt-auto">
        <div className="flex items-center justify-center gap-2">
          <div className="w-2 h-2 rounded-full bg-[#667eea] transition-all"></div>
          <div className="w-2 h-2 rounded-full bg-[#667eea] transition-all"></div>
          <div className="w-8 h-2 rounded bg-[#667eea] transition-all"></div>
          <div className="w-2 h-2 rounded-full bg-gray-300 transition-all"></div>
        </div>

        <div className="flex items-center justify-between gap-3">
          <button type="button" className="bg-transparent text-gray-700 underline hover:text-gray-900 transition-colors" onClick={handleBack}>
            Back
          </button>
          <button
            type="button"
            className="px-6 py-2.5 text-sm font-semibold rounded-lg cursor-pointer transition-all border-none flex items-center justify-center bg-gradient-to-br from-[#667eea] to-[#764ba2] text-white hover:translate-y-[-2px] hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            onClick={handleNext}
            disabled={!names.some(n => n.trim()) || !imagePreviewUrl}
          >
            Next
          </button>
        </div>
      </div>

      {/* Image Library Modal */}
      <ImageLibraryModal
        isOpen={showLibraryModal}
        onClose={() => setShowLibraryModal(false)}
        onSelectImage={handleLibraryImageSelect}
      />
    </div>
  );
}
